import { useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router";
import { Menu, Spin } from "antd";
import { LeftOutlined, GlobalOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";

import { useScopeParams } from "@/hooks/useScopeParams";
import { apiFetchJson } from "@/utils/apiFetch";
import type { MasteredSite, GeoLevel, GeoAggregateTrialEntry } from "./types";
import { createGeoSiteMenuItems } from "./utils/geoTabItems";
import { buildGeoProfileTabUrl, siteRegions } from "./utils/geoNavigation";
import GeoSitesTab from "./tabs/GeoSitesTab";

const DEFAULT_TAB = "sites";

async function fetchPortfolioSites(): Promise<MasteredSite[]> {
  return apiFetchJson<MasteredSite[]>(
    `${import.meta.env.VITE_API_URL}/data/portfolio/portfolio-sites/`,
  );
}

function computeGeoAggregateEntries(
  sites: MasteredSite[],
): GeoAggregateTrialEntry[] {
  const byTrial: Record<
    string,
    { siteKeys: Set<string>; entries: MasteredSite["trials"][number][] }
  > = {};

  for (const site of sites) {
    for (const entry of site.trials) {
      if (!byTrial[entry.trial_id]) {
        byTrial[entry.trial_id] = { siteKeys: new Set(), entries: [] };
      }
      byTrial[entry.trial_id].siteKeys.add(site.site_key);
      byTrial[entry.trial_id].entries.push(entry);
    }
  }

  return Object.entries(byTrial).map(([trial_id, { siteKeys, entries }]) => {
    const site_count = siteKeys.size;
    const total_enrolled = entries.reduce((s, e) => s + (e.enrolled ?? 0), 0);
    const total_target = entries.reduce((s, e) => s + (e.target ?? 0), 0);
    const total_ae_count = entries.reduce((s, e) => s + (e.ae_count ?? 0), 0);
    const total_open_pds = entries.reduce((s, e) => s + (e.open_pds ?? 0), 0);
    const total_open_queries = entries.reduce(
      (s, e) => s + (e.open_queries ?? 0),
      0,
    );
    const avg_ae_rate_pct =
      entries.length > 0
        ? Math.round(
            (entries.reduce((s, e) => s + (e.ae_rate_pct ?? 0), 0) /
              entries.length) *
              10,
          ) / 10
        : 0;
    const avg_risk_score =
      entries.length > 0
        ? Math.round(
            entries.reduce((s, e) => s + (e.risk_score ?? 0), 0) /
              entries.length,
          )
        : 0;
    const avg_crf_completion_pct =
      entries.length > 0
        ? Math.round(
            entries.reduce((s, e) => s + (e.crf_completion_pct ?? 0), 0) /
              entries.length,
          )
        : 0;
    const most_recent_monitoring_visit =
      entries
        .map((e) => e.last_monitoring_visit)
        .sort()
        .at(-1) ?? "";

    return {
      trial_id,
      site_count,
      total_enrolled,
      total_target,
      avg_ae_rate_pct,
      total_ae_count,
      total_open_pds,
      avg_risk_score,
      most_recent_monitoring_visit,
      total_open_queries,
      avg_crf_completion_pct,
    };
  });
}

export default function CrossTrialGeoProfile() {
  const {
    geoType,
    geoValue: encodedGeoValue,
    tab,
  } = useParams<{
    geoType: string;
    geoValue: string;
    tab?: string;
  }>();
  const { scope, id, buildUrl } = useScopeParams();
  const navigate = useNavigate();

  const activeTab = tab ?? DEFAULT_TAB;
  const geoValue = encodedGeoValue ? decodeURIComponent(encodedGeoValue) : "";

  const { data: allSites = [], isLoading: sitesLoading } = useQuery({
    queryKey: ["portfolio-sites", scope, id],
    queryFn: () => fetchPortfolioSites(),
    enabled: !!scope && !!id,
    staleTime: 5 * 60 * 1000,
  });

  const filteredSites = useMemo(() => {
    if (!geoType || !geoValue) return [];
    if (geoType === "region")
      return allSites.filter((s) => siteRegions(s).includes(geoValue));
    if (geoType === "country")
      return allSites.filter((s) => s.country === geoValue);
    return [];
  }, [geoType, geoValue, allSites]);

  const trialEntries = useMemo(
    () => computeGeoAggregateEntries(filteredSites),
    [filteredSites],
  );

  const menuItems = useMemo(() => createGeoSiteMenuItems(), []);

  const totalEnrolled = useMemo(
    () => trialEntries.reduce((s, e) => s + e.total_enrolled, 0),
    [trialEntries],
  );

  const countries = useMemo(() => {
    if (geoType === "region") {
      return [...new Set(filteredSites.map((s) => s.country))].sort();
    }
    return [];
  }, [geoType, filteredSites]);

  const regionLabel = useMemo(() => {
    if (geoType === "country" && filteredSites.length > 0) {
      return siteRegions(filteredSites[0]).join(" · ");
    }
    return "";
  }, [geoType, filteredSites]);

  if (sitesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  if (!filteredSites.length) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400 text-[13px]">
        Geography not found.
      </div>
    );
  }

  const handleMenuSelect = (key: string) => {
    navigate(
      buildUrl(buildGeoProfileTabUrl(geoType as GeoLevel, geoValue, key)),
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case "sites":
        return <GeoSitesTab sites={filteredSites} />;
      default:
        return <GeoSitesTab sites={filteredSites} />;
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Back link — spans full width above both columns */}
      <div className="shrink-0 px-3 pt-2">
        <Link
          to={buildUrl("site-profiles")}
          className="flex items-center gap-1.5 text-[12px] text-[#556C75] hover:text-[#2B4752] py-1 w-fit"
        >
          <LeftOutlined className="text-[10px]" />
          All Sites
        </Link>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <div className="w-[260px] min-w-[260px] sticky top-0 self-start h-screen flex flex-col gap-2 p-3 pb-6 overflow-y-auto">
          {/* Geo info card */}
          <div
            className="px-3 py-3 flex flex-col gap-1"
            style={{
              borderRadius: 8,
              border: "1px solid rgba(0, 0, 0, 0.04)",
              background: "#FFF",
              boxShadow: "0 2px 15px 4px rgba(0, 0, 0, 0.02)",
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              <div className="flex items-center p-[5px] rounded-[4px] bg-[#2B4752] text-white">
                <GlobalOutlined style={{ fontSize: 20 }} />
              </div>
              <span className="text-[10px] font-semibold text-[#556C75] tracking-wide uppercase">
                {geoType === "region" ? "Region" : "Country"}
              </span>
            </div>
            <span className="text-[14px] font-semibold text-[#1F2933] leading-tight">
              {geoValue}
            </span>
            {geoType === "region" && countries.length > 0 && (
              <span className="text-[11px] text-[#8C8C8C]">
                {countries.join(", ")}
              </span>
            )}
            {geoType === "country" && regionLabel && (
              <span className="text-[11px] text-[#8C8C8C]">{regionLabel}</span>
            )}

            <div className="flex items-center gap-3 mt-2 pt-2 border-t border-[#F0F2F4]">
              <div className="flex flex-col items-center">
                <span className="text-[16px] font-semibold text-[#1F2933]">
                  {filteredSites.length}
                </span>
                <span className="text-[10px] text-[#8C8C8C] uppercase tracking-wide">
                  Sites
                </span>
              </div>
              <div className="w-px h-8 bg-[#F0F2F4]" />
              <div className="flex flex-col items-center">
                <span className="text-[16px] font-semibold text-[#1F2933]">
                  {trialEntries.length}
                </span>
                <span className="text-[10px] text-[#8C8C8C] uppercase tracking-wide">
                  Trials
                </span>
              </div>
              <div className="w-px h-8 bg-[#F0F2F4]" />
              <div className="flex flex-col items-center">
                <span className="text-[16px] font-semibold text-[#1F2933]">
                  {totalEnrolled}
                </span>
                <span className="text-[10px] text-[#8C8C8C] uppercase tracking-wide">
                  Enrolled
                </span>
              </div>
            </div>
          </div>

          {/* Navigation menu */}
          <div
            className="overflow-y-auto overflow-clip"
            style={{
              borderRadius: 8,
              border: "1px solid rgba(0, 0, 0, 0.04)",
              background: "#FFF",
              boxShadow: "0 2px 15px 4px rgba(0, 0, 0, 0.02)",
            }}
          >
            <Menu
              mode="inline"
              selectedKeys={[activeTab]}
              items={menuItems}
              onClick={({ key }) => handleMenuSelect(key)}
              style={{ borderRight: "none", background: "transparent" }}
            />
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex-1 p-3">{renderContent()}</div>
        </div>
      </div>
    </div>
  );
}
